import { Download } from 'lucide-react'
import { AccountingSyncChip } from '@/components/AccountingSyncChip'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export type PayoutStatusFilter =
  | 'all'
  | 'recorded'
  | 'needs_attention'
  | 'needs_mapping'
  | 'ready_for_deposit'
  | 'deposited'
  | 'error'
  | 'drift'
  | 'action_required'

const payoutStatusFilters: { value: PayoutStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'recorded', label: 'Recorded' },
  { value: 'needs_attention', label: 'Needs attention' },
  { value: 'needs_mapping', label: 'Needs mapping' },
  { value: 'ready_for_deposit', label: 'Ready for deposit' },
  { value: 'deposited', label: 'Deposited' },
  { value: 'error', label: 'Error' },
  { value: 'drift', label: 'Drift' },
  { value: 'action_required', label: 'Action required' },
]

export function PayoutListToolbar({
  statusFilter,
  onStatusFilterChange,
  summary,
}: {
  statusFilter: PayoutStatusFilter
  onStatusFilterChange: (status: PayoutStatusFilter) => void
  summary?: { count: number; net: string | null }
}) {
  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="payout-status-filter">Status</Label>
          <Select
            value={statusFilter}
            onValueChange={(value) => onStatusFilterChange(value as PayoutStatusFilter)}
          >
            <SelectTrigger id="payout-status-filter" className="h-9 w-44" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {payoutStatusFilters.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button className="gap-2" variant="outline" asChild>
          <a href={payoutExportPath(statusFilter)} download>
            <Download className="h-4 w-4" />
            Export CSV
          </a>
        </Button>
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-caption text-muted-foreground">{payoutSummaryLabel(summary)}</p>
        <AccountingSyncChip />
      </div>
    </div>
  )
}

export function payoutListPath(status: PayoutStatusFilter) {
  return status === 'all' ? '/api/v1/payouts' : `/api/v1/payouts?status=${status}`
}

function payoutExportPath(status: PayoutStatusFilter) {
  return status === 'all' ? '/api/v1/payouts/export' : `/api/v1/payouts/export?status=${status}`
}

function payoutSummaryLabel(summary?: { count: number; net: string | null }) {
  if (!summary) return '— payouts'

  const countLabel = summary.count === 1 ? 'payout' : 'payouts'
  return `${summary.count} ${countLabel}${summary.net ? ` · ${summary.net} net` : ''}`
}
